'use client';

import React, { useState, useEffect } from 'react';
import BlogHero from './BlogHero';
import CategoryFilter from './CategoryFilter';
import FeaturedPost from './FeaturedPost';
import BlogCard from './BlogCard';
import ResourceLibrary from './ResourceLibrary';
import NewsletterCTA from './NewsletterCTA';

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: {
    name: string;
    avatar: string;
    avatarAlt: string;
  };
  publishDate: string;
  readTime: string;
  image: string;
  imageAlt: string;
  tags: string[];
}

interface Resource {
  id: string;
  title: string;
  description: string;
  type: string;
  icon: string;
  downloadUrl: string;
}

const BlogInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories: Category[] = [
  { id: 'all', name: 'Alle Artikelen', icon: 'Squares2X2Icon', count: 24 },
  { id: 'implementation', name: 'Implementatie', icon: 'CogIcon', count: 8 },
  { id: 'market', name: 'Marktanalyse', icon: 'ChartBarIcon', count: 6 },
  { id: 'compliance', name: 'Compliance', icon: 'ShieldCheckIcon', count: 4 },
  { id: 'trends', name: 'Trends', icon: 'SparklesIcon', count: 6 }];


  const featuredPost: BlogPost = {
    id: 'ai-transformation-benelux-2026',
    title: 'AI-transformatie in de Benelux: Wat MKB-bedrijven moeten weten in 2026',
    excerpt: 'Een diepgaande analyse van de huidige AI-trends in Nederland en België, met praktische inzichten voor kleine en middelgrote bedrijven die hun digitale transformatie willen versnellen. Ontdek welke AI-oplossingen het meeste ROI opleveren en hoe u compliance kunt waarborgen.',
    category: 'Marktanalyse',
    author: {
      name: 'Sophie van der Berg',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14da91c34-1763294780479.png",
      avatarAlt: 'Professional headshot of woman with long brown hair in navy blazer smiling at camera'
    },
    publishDate: '20 januari 2026',
    readTime: '12 min',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15afdb008-1772093387845.png",
    imageAlt: 'Futuristic digital network visualization with glowing blue connections over dark background representing AI technology',
    tags: ['AI-strategie', 'Benelux', 'MKB']
  };

  const blogPosts: BlogPost[] = [
  {
    id: 'chatbot-implementation-guide',
    title: 'Complete gids: AI-chatbot implementeren in 30 dagen',
    excerpt: 'Stap-voor-stap handleiding voor het succesvol implementeren van een AI-chatbot in uw klantenservice, inclusief ROI-berekeningen en best practices.',
    category: 'Implementatie',
    author: {
      name: 'Mark Janssen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d90a96ad-1763299909299.png",
      avatarAlt: 'Professional headshot of man with short brown hair in gray suit smiling confidently'
    },
    publishDate: '18 januari 2026',
    readTime: '8 min',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b71551de-1766565517422.png",
    imageAlt: 'Modern chatbot interface on smartphone screen with blue message bubbles and AI assistant icon',
    tags: ['chatbot', 'klantenservice', 'implementatie']
  },
  {
    id: 'gdpr-ai-compliance',
    title: 'GDPR en AI: Compliance-checklist voor Nederlandse bedrijven',
    excerpt: 'Alles wat u moet weten over GDPR-compliance bij het gebruik van AI-systemen, met praktische tips en juridische overwegingen.',
    category: 'Compliance',
    author: {
      name: 'Lisa de Vries',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1985c262f-1763294244026.png",
      avatarAlt: 'Professional headshot of woman with blonde hair in white blouse smiling warmly'
    },
    publishDate: '15 januari 2026',
    readTime: '10 min',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1947eb71b-1767698053789.png",
    imageAlt: 'Legal documents and gavel on desk with digital security icons overlay representing GDPR compliance',
    tags: ['GDPR', 'compliance', 'juridisch']
  },
  {
    id: 'ai-cost-savings-calculator',
    title: 'ROI van AI: Hoe u besparingen kunt berekenen',
    excerpt: 'Praktische methoden en tools om de return on investment van uw AI-implementatie te meten en te maximaliseren.',
    category: 'Implementatie',
    author: {
      name: 'Thomas Bakker',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d90a96ad-1763299909299.png",
      avatarAlt: 'Professional headshot of man with dark hair and beard in blue shirt smiling professionally'
    },
    publishDate: '12 januari 2026',
    readTime: '6 min',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1451a6a62-1768305328630.png",
    imageAlt: 'Financial charts and graphs on laptop screen showing upward growth trends and ROI calculations',
    tags: ['ROI', 'financieel', 'calculatie']
  },
  {
    id: 'ai-trends-2026',
    title: '5 AI-trends die uw bedrijf niet mag missen in 2026',
    excerpt: 'Ontdek de belangrijkste AI-ontwikkelingen die dit jaar impact zullen hebben op MKB-bedrijven in de Benelux.',
    category: 'Trends',
    author: {
      name: 'Emma Vermeulen',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17187f2d5-1763301571161.png",
      avatarAlt: 'Professional headshot of woman with red hair in black blazer smiling confidently'
    },
    publishDate: '10 januari 2026',
    readTime: '7 min',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15afdb008-1772093387845.png",
    imageAlt: 'Futuristic technology concept with glowing digital elements and AI neural network visualization',
    tags: ['trends', 'innovatie', '2026']
  },
  {
    id: 'automation-workflow-guide',
    title: 'Workflow-automatisering met AI: Praktische voorbeelden',
    excerpt: 'Concrete use cases en implementatiestrategieën voor het automatiseren van bedrijfsprocessen met AI-technologie.',
    category: 'Implementatie',
    author: {
      name: 'David Smit',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17fc21307-1763293959943.png",
      avatarAlt: 'Professional headshot of man with short blonde hair in white shirt smiling warmly'
    },
    publishDate: '8 januari 2026',
    readTime: '9 min',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10e23d774-1772731280616.png",
    imageAlt: 'Automated workflow diagram on computer screen with connected process nodes and data flow arrows',
    tags: ['automatisering', 'workflow', 'efficiency']
  },
  {
    id: 'belgian-ai-market-analysis',
    title: 'AI-adoptie in België: Marktonderzoek en kansen',
    excerpt: 'Diepgaande analyse van de Belgische AI-markt met specifieke kansen voor Vlaamse en Waalse bedrijven.',
    category: 'Marktanalyse',
    author: {
      name: 'Sophie van der Berg',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14da91c34-1763294780479.png",
      avatarAlt: 'Professional headshot of woman with long brown hair in navy blazer smiling at camera'
    },
    publishDate: '5 januari 2026',
    readTime: '11 min',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1573170ce-1769260711177.png",
    imageAlt: 'Belgium map with digital network connections and data points showing AI market distribution',
    tags: ['België', 'marktonderzoek', 'kansen']
  }];


  const resources: Resource[] = [
  {
    id: 'ai-readiness-assessment',
    title: 'AI Readiness Assessment Tool',
    description: 'Evalueer de AI-gereedheid van uw organisatie met deze uitgebreide checklist en scorekaart.',
    type: 'Template',
    icon: 'ClipboardDocumentCheckIcon',
    downloadUrl: '#'
  },
  {
    id: 'roi-calculator',
    title: 'ROI Calculator Spreadsheet',
    description: 'Bereken de verwachte return on investment van uw AI-implementatie met deze Excel-template.',
    type: 'Tool',
    icon: 'CalculatorIcon',
    downloadUrl: '#'
  },
  {
    id: 'implementation-guide',
    title: 'AI Implementatie Stappenplan',
    description: 'Gedetailleerde gids met alle stappen voor een succesvolle AI-implementatie in uw bedrijf.',
    type: 'Gids',
    icon: 'DocumentTextIcon',
    downloadUrl: '#'
  },
  {
    id: 'compliance-checklist',
    title: 'GDPR Compliance Checklist',
    description: 'Controleer of uw AI-systemen voldoen aan alle GDPR-vereisten met deze praktische checklist.',
    type: 'Checklist',
    icon: 'ShieldCheckIcon',
    downloadUrl: '#'
  }];


  const filteredPosts = isHydrated ?
  blogPosts.filter((post) => {
    const matchesCategory =
    activeCategory === 'all' || post.category.toLowerCase() === categories.find((c) => c.id === activeCategory)?.name.toLowerCase();
    const matchesSearch =
    searchQuery === '' ||
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  }) :
  blogPosts;

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <BlogHero searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <CategoryFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory} />


        <FeaturedPost post={featuredPost} />

        <section className="mb-16">
          <h2 className="text-2xl font-display font-bold text-foreground mb-8">
            Laatste Artikelen
          </h2>
          {filteredPosts.length > 0 ?
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) =>
            <BlogCard key={post.id} post={post} />
            )}
            </div> :

          <div className="text-center py-16">
              <p className="text-lg text-muted-foreground">
                Geen artikelen gevonden die aan uw zoekcriteria voldoen.
              </p>
            </div>
          }
        </section>

        <ResourceLibrary resources={resources} />

        <NewsletterCTA />
      </div>
    </div>);

};

export default BlogInteractive;